import PropTypes from "prop-types";

import styled from "styled-components/macro";

import { useRecurringGifts } from "CrossFeatureComponents/UserRecurringGifts/RecurringGiftsContext";
import { useManageMyGivingPage } from "Giving/ManageMyGivingPage/ManageMyGivingPageContext";
import classNames from "classnames";
import { motion } from "framer-motion";

import { formatAmountForDisplay } from "Utilities/Amount/formatAmountForDisplay";

import NextPaymentBox from "./NextPaymentBox";
import StatBox from "./StatBox";
import ThirtyDayTotal from "./ThirtyDayTotal";
import { useGetNextPayments } from "./useGetNextPayments";

GivingSummary.propTypes = {
  isMobile: PropTypes.bool,
};

export default function GivingSummary({ isMobile }) {
  const {
    state: { userID, user, givingStats, pageSettings },
  } = useManageMyGivingPage();
  const { state: recurringState } = useRecurringGifts();

  const yearToDate = givingStats?.yearToDate;
  const showYearToDate = pageSettings?.showYearToDate && yearToDate;
  const nextPayments = useGetNextPayments();

  const yearToDateDisplayValue =
    yearToDate &&
    `$${formatAmountForDisplay(yearToDate, {
      roundToWholeDollar: true,
    })}`;
  const noStatsToShow = !showYearToDate && nextPayments.length === 0;

  const getLine1Text = () => {
    if (user?.householdSize < 2) {
      return `${user.firstName} ${user.lastName}`;
    }
    return user?.lastName;
  };

  const getLine2Text = () => {
    if (user?.householdSize < 2) return "Giving";
    return "Household Giving";
  };

  const statsAnimationVariants = {
    visible: {
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.1,
      },
    },
    hidden: {},
  };

  const readyToShowStats =
    userID &&
    recurringState.recurringGiftsListStatus === "success" &&
    !noStatsToShow;

  return (
    <Wrapper
      className={classNames({ no_stats: noStatsToShow, mobile: isMobile })}
    >
      <Header>
        <Intro
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0, transition: { type: "tween" } }}
        >
          <Line1>{getLine1Text()}</Line1>
          <Line2>{getLine2Text()}</Line2>
        </Intro>
      </Header>

      {readyToShowStats ? (
        <StatsContainer
          initial="hidden"
          animate="visible"
          variants={statsAnimationVariants}
        >
          {showYearToDate && (
            <StatBox
              key="ytd"
              text="Given Year-to-Date"
              number={yearToDateDisplayValue}
            />
          )}

          <NextPaymentBox payments={nextPayments} />

          <ThirtyDayTotal />
        </StatsContainer>
      ) : null}
    </Wrapper>
  );
}

const Wrapper = styled.div`
  padding: 0 20px;
  padding-bottom: 40px;
  &.mobile {
    padding: 0;
    display: block;
    padding-bottom: 60px;
  }
`;

const Header = styled.div``;
const Intro = styled(motion.div)`
  flex-shrink: 0;
  padding-right: 20px;
  color: var(--textPrimary);
`;

const Line1 = styled.div`
  font-size: 52px;
  font-weight: 600;
  line-height: 56px;
  @media (max-width: 540px) {
    font-size: 42px;
    line-height: 42px;
  }
`;
const Line2 = styled.div`
  font-size: 32px;
  @media (max-width: 540px) {
    font-size: 26px;
  }
`;

const StatsContainer = styled(motion.div)`
  margin-top: 40px;
  display: flex;
  flex-direction: column;
  gap: 40px;
  align-items: flex-start;
  ${Wrapper}.mobile & {
    flex-direction: row;
    flex-wrap: wrap;
    gap: 60px;

    @media (max-width: 540px) {
      margin-top: 30px;
      gap: 30px;
      align-items: flex-start;
      flex-direction: column;
    }
  }
`;
